/**
 * ops 新規依頼フォームの下書き（localStorage）の復元・自動保存・手動保存・破棄。フォーム本体（ops.submit）から
 * 下書きの扱いだけを切り出したもので、保存先の読み書きは lib/ops-draft が持つ。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 新規依頼フォームの下書きをマウント時に 1 度だけ復元し、入力があるあいだ 500ms デバウンスで自動保存し、「下書き保存」の手動保存（2 秒の保存済み表示）と破棄を提供する。補足対応・送信後は保存しない（呼び出し側が保存対象を null にする）
 * @graph-connects loadOpsDraft [calls] 下書きの復元
 * @graph-connects saveOpsDraft [calls] 下書きの保存
 * @graph-connects clearOpsDraft [calls] 下書きの破棄
 */
import { useEffect, useState } from 'react';
import {
  clearOpsDraft,
  draftHasContent,
  loadOpsDraft,
  saveOpsDraft,
  type OpsSubmitDraft,
} from '../lib/ops-draft.js';

/** 下書きとして保存するフォーム入力（保存時刻を除く）。 @graph-connects none */
export type OpsSubmitDraftFields = Omit<OpsSubmitDraft, 'savedAt'>;

/**
 * マウント時に下書きを 1 度だけ読む（中身がある時だけ復元対象にする）。enabled=false なら読まない。
 * @graph-connects loadOpsDraft [calls] 下書きの復元
 */
export function useRestoredOpsDraft(enabled: boolean): OpsSubmitDraft | null {
  const [restored] = useState<OpsSubmitDraft | null>(() => {
    const found = enabled ? loadOpsDraft() : null;
    return found !== null && draftHasContent(found) ? found : null;
  });
  return restored;
}

/** 下書きの保存状態と操作。 @graph-connects none */
export interface OpsDraftAutosave {
  /** 復元した下書きの保存時刻（バナー表示用）。破棄・送信で null。 */
  notice: number | null;
  /** 手動保存の直後 2 秒だけ true（「保存しました」表示用）。 */
  savedFlash: boolean;
  /** 今の入力を下書きとして保存する（保存対象が null なら何もしない）。 */
  save: () => void;
  /** 下書きを破棄してバナーを消す。 */
  discard: () => void;
}

/**
 * 入力（fields）を 500ms デバウンスで下書きへ自動保存する。fields が null のあいだ（補足対応・送信後）は
 * 保存しない。入力の比較は値で行う（毎レンダーで作り直される object でもタイマーを張り直さない）。
 * @graph-connects saveOpsDraft [calls] 下書きの保存
 * @graph-connects clearOpsDraft [calls] 下書きの破棄
 */
export function useOpsDraftAutosave(
  fields: OpsSubmitDraftFields | null,
  restoredAt: number | null,
): OpsDraftAutosave {
  const [notice, setNotice] = useState<number | null>(restoredAt);
  const [savedFlash, setSavedFlash] = useState(false);
  const key = fields === null ? null : JSON.stringify(fields);

  useEffect(() => {
    if (key === null) return;
    const snapshot = JSON.parse(key) as OpsSubmitDraftFields;
    if (!draftHasContent({ ...snapshot, savedAt: 0 })) return;
    const timer = setTimeout(() => {
      saveOpsDraft({ ...snapshot, savedAt: Date.now() });
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [key]);

  // 「保存しました」の一時表示を 2 秒で消す。
  useEffect(() => {
    if (!savedFlash) return;
    const timer = setTimeout(() => {
      setSavedFlash(false);
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [savedFlash]);

  return {
    notice,
    savedFlash,
    save: () => {
      if (fields === null) return;
      saveOpsDraft({ ...fields, savedAt: Date.now() });
      setSavedFlash(true);
    },
    discard: () => {
      clearOpsDraft();
      setNotice(null);
    },
  };
}
